import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Heart, Lock } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIERS = [
  { id: "transport", amount: "R150", label: "Transport for a learner to an excursion" },
  { id: "books", amount: "R500", label: "Books and stationery for a club member" },
  { id: "tablets", amount: "R1,500", label: "Tablets and resources for a club session" },
  { id: "learner_year", amount: "R4,427", label: "Sponsor one learner for a full year" },
];

const submitToPayFast = (paymentUrl, fields) => {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = paymentUrl;
  Object.entries(fields).forEach(([name, value]) => {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
};

export const DonateButton = ({ variant = "solid", size = "md", testId = "donate-modal-trigger", children }) => {
  const [open, setOpen] = useState(false);
  const [tier, setTier] = useState(1);
  const [customAmount, setCustomAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const isCustom = tier === TIERS.length;

  const donate = async () => {
    setError("");
    if (isCustom) {
      const value = Number(customAmount);
      if (!value || value < 10) {
        setError("Please enter an amount of at least R10.");
        return;
      }
    }
    setBusy(true);
    try {
      const payload = isCustom
        ? { tier_id: "custom", custom_amount: Number(customAmount) }
        : { tier_id: TIERS[tier].id };
      const { data } = await axios.post(`${API}/payments/create`, payload);
      submitToPayFast(data.payment_url, data.fields);
    } catch (err) {
      setError("Could not start the payment. Please try again.");
      setBusy(false);
    }
  };

  const buttonLabel = isCustom
    ? customAmount && Number(customAmount) >= 10
      ? `Donate R${Number(customAmount).toLocaleString("en-ZA")}`
      : "Donate your amount"
    : `Donate ${TIERS[tier].amount}`;

  const base =
    variant === "solid"
      ? "bg-ember text-sand hover:bg-ember-hover shadow-[0_8px_24px_-8px_rgba(217,93,57,0.6)]"
      : "border border-sand/40 text-sand hover:bg-sand hover:text-coal";

  const sizeCls = size === "lg" ? "px-9 py-4 text-base" : "px-6 py-2.5 text-sm";

  return (
    <>
      <button
        data-testid={testId}
        onClick={() => setOpen(true)}
        className={`${base} ${sizeCls} rounded-full font-heading font-bold transition-colors duration-300 inline-flex items-center gap-2`}
      >
        <Heart size={15} className="fill-current" />
        {children || "Donate"}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[90] flex items-center justify-center bg-coal-dark/80 backdrop-blur-sm p-4"
            onClick={() => setOpen(false)}
            data-testid="donate-modal"
          >
            <motion.div
              initial={{ opacity: 0, y: 40, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 30, scale: 0.97 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="relative w-full max-w-lg rounded-3xl bg-sand p-8 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                data-testid="donate-modal-close"
                onClick={() => setOpen(false)}
                className="absolute right-5 top-5 text-coal-muted hover:text-coal transition-colors"
                aria-label="Close donate dialog"
              >
                <X size={20} />
              </button>
              <p className="font-mono-brand uppercase tracking-[0.25em] text-xs font-bold text-ember mb-2">
                Fuel the movement
              </p>
              <h3 className="font-display font-extrabold text-2xl text-coal tracking-tight mb-2">
                Keep English is Lekka running
              </h3>
              <p className="text-coal-muted text-sm leading-relaxed mb-6">
                Donations give learners access to transport, food and the resources that make the
                magic possible, books, tablets, stationery, and entrance into places like Robben
                Island.
              </p>
              <div className="grid grid-cols-2 gap-3 mb-4">
                {TIERS.map((t, i) => (
                  <button
                    key={t.id}
                    data-testid={`donate-tier-${i}`}
                    onClick={() => setTier(i)}
                    className={`rounded-2xl border p-4 text-left transition-all duration-300 ${
                      tier === i
                        ? "border-ember bg-ember-subtle shadow-[0_0_0_1px_#D95D39]"
                        : "border-coal/10 bg-white hover:border-ember/40"
                    }`}
                  >
                    <span className="font-display font-extrabold text-lg text-coal block">{t.amount}</span>
                    <span className="text-xs text-coal-muted leading-snug block mt-1">{t.label}</span>
                  </button>
                ))}
              </div>
              <button
                data-testid="donate-tier-custom"
                onClick={() => setTier(TIERS.length)}
                className={`mb-4 w-full rounded-2xl border p-4 text-left transition-all duration-300 ${
                  isCustom
                    ? "border-ember bg-ember-subtle shadow-[0_0_0_1px_#D95D39]"
                    : "border-coal/10 bg-white hover:border-ember/40"
                }`}
              >
                <span className="font-display font-extrabold text-lg text-coal block">Your own amount</span>
                <span className="text-xs text-coal-muted leading-snug block mt-1">Every rand counts, give what feels right</span>
              </button>
              {isCustom && (
                <div className="mb-4 flex items-center gap-2 rounded-2xl border border-coal/15 bg-white px-4 py-3">
                  <span className="font-display font-extrabold text-lg text-coal-muted">R</span>
                  <input
                    data-testid="donate-custom-amount-input"
                    type="number"
                    min="10"
                    step="1"
                    value={customAmount}
                    onChange={(e) => setCustomAmount(e.target.value)}
                    placeholder="Enter amount, e.g. 250"
                    className="w-full bg-transparent font-heading text-lg font-bold text-coal outline-none"
                  />
                </div>
              )}
              {error && (
                <p className="mb-4 rounded-xl bg-ember-subtle px-4 py-2.5 text-center text-xs font-semibold text-ember" data-testid="donate-error">
                  {error}
                </p>
              )}
              <button
                data-testid="donate-payfast-button"
                onClick={donate}
                disabled={busy}
                className="w-full rounded-full bg-ember py-3.5 font-heading font-bold text-sand hover:bg-ember-hover transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60"
              >
                <Lock size={15} />
                {busy ? "Connecting securely..." : buttonLabel}
              </button>
              <p className="text-center text-xs text-coal-muted mt-3">
                Secure payment via PayFast: cards, instant EFT and more.
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
